import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import supabase from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      checkAuthStatus();
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const formatUser = (userData) => {
    if (!userData) return null;
    
    // Extract role name safely. 
    // Prioritize the relationship name, fall back to the text column, then default.
    const roleName = userData.roles_crm_2025?.name || userData.role || 'Employee';
    
    // Normalize role name for comparisons (handle 'admin' vs 'Admin')
    const normalizedRole = roleName.charAt(0).toUpperCase() + roleName.slice(1).toLowerCase();

    const permissions = userData.roles_crm_2025?.permissions 
      ? (typeof userData.roles_crm_2025.permissions === 'string' 
          ? JSON.parse(userData.roles_crm_2025.permissions) 
          : userData.roles_crm_2025.permissions)
      : {};

    return {
      ...userData,
      role: normalizedRole, // Ensure consistent capitalization (Admin, Manager, Employee)
      originalRole: userData.role, // Keep original for reference
      roleId: userData.role_id,
      permissions: permissions
    };
  };

  const checkAuthStatus = async () => {
    try {
      const sessionToken = localStorage.getItem('crm_session_token');
      if (!sessionToken) {
        setLoading(false);
        return;
      }

      // Try fetching with role AND department relationship
      let { data, error } = await supabase
        .from('user_sessions_crm_2025')
        .select(`
          *,
          users_crm_2025 (
            *,
            roles_crm_2025 ( id, name, permissions ),
            departments_crm_2025 ( id, name )
          )
        `)
        .eq('session_token', sessionToken)
        .gt('expires_at', new Date().toISOString())
        .single();

      // Fallback: If relationship fetch fails
      if (error) {
        console.warn('Complex auth check failed, trying fallback...', error.message);
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('user_sessions_crm_2025')
          .select(`
            *,
            users_crm_2025 (*)
          `)
          .eq('session_token', sessionToken)
          .gt('expires_at', new Date().toISOString())
          .single();
        
        if (fallbackError) throw fallbackError;
        data = fallbackData;
      }

      if (!data || !data.users_crm_2025) {
        localStorage.removeItem('crm_session_token');
        setLoading(false);
        return;
      }

      const formattedUser = formatUser(data.users_crm_2025);
      setUser(formattedUser);
      
      // Update last login (fire and forget)
      supabase
        .from('users_crm_2025')
        .update({ last_login: new Date().toISOString() })
        .eq('id', data.users_crm_2025.id)
        .then(({ error }) => {
          if (error) console.error('Failed to update last login:', error);
        });

    } catch (error) {
      console.error('Auth check error:', error);
      localStorage.removeItem('crm_session_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      let userData;

      // 1. Try fetching user with Role AND Department relationship
      const { data: complexData, error: complexError } = await supabase
        .from('users_crm_2025')
        .select(`
          *,
          roles_crm_2025 ( id, name, permissions ),
          departments_crm_2025 ( id, name )
        `)
        .eq('email', email.toLowerCase())
        .eq('is_active', true)
        .single();

      if (!complexError && complexData) {
        userData = complexData;
      } else {
        // 2. Fallback: Fetch user without relationship if the above failed
        console.warn('Login: Role/Dept relationship fetch failed, attempting simple fetch.');
        const { data: simpleData, error: simpleError } = await supabase
          .from('users_crm_2025')
          .select('*')
          .eq('email', email.toLowerCase())
          .eq('is_active', true)
          .single();

        if (simpleError || !simpleData) {
          throw new Error('Invalid email or password');
        }
        userData = simpleData;
      }

      // STRICT VALIDATION: Only check against the stored password hash.
      // Removed hardcoded "OR" conditions to prevent old/default passwords from working 
      // after a database update.
      const isValidPassword = (password === userData.password_hash);

      if (!isValidPassword) {
        throw new Error('Invalid email or password');
      }

      // Create Session
      const sessionToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 8);

      const { error: sessionError } = await supabase
        .from('user_sessions_crm_2025')
        .insert([
          { user_id: userData.id, session_token: sessionToken, expires_at: expiresAt.toISOString() }
        ]);

      if (sessionError) throw sessionError;

      localStorage.setItem('crm_session_token', sessionToken);
      const formattedUser = formatUser(userData);
      setUser(formattedUser);
      toast.success(`Welcome back, ${userData.full_name}!`);
      return formattedUser;

    } catch (error) {
      console.error('Login error:', error);
      // We do NOT toast here anymore to allow the UI to handle the error display
      // tailored to the specific form.
      throw error;
    }
  };

  const logout = async () => {
    try {
      const sessionToken = localStorage.getItem('crm_session_token');
      if (sessionToken) {
        await supabase
          .from('user_sessions_crm_2025')
          .delete()
          .eq('session_token', sessionToken);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('crm_session_token');
      setUser(null);
      toast.success('Logged out successfully');
    }
  };

  // Helper flags
  const isAdmin = user?.role === 'Admin';
  const isManager = isAdmin || user?.role === 'Manager';

  const value = {
    user,
    loading,
    login,
    logout,
    isAdmin,
    isManager,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};